"use client";

// components/context/overlay-context.tsx
//
// Delivery-layer wiring for the two-slot overlay FSM (lib/overlay/overlay-state.ts).
// One `OverlayProvider` mounted in the ROOT app/layout.tsx owns the panel +
// utility slots for the whole app shell. Root, not (site)/layout: overlay
// consumers are reachable from the root-mounted global checkout dialogs
// (GlobalQuickCheckout/GlobalOfferCheckout → CheckoutForm → PayPalCheckout),
// so a (site)-scoped provider left them uncovered — runtime throw on
// buy-now Pay from /feed and /search (2026-07-24). Every consumer reads/writes
// through `useOverlay()` / `usePanelIs()` / `useUtilityIs()` — never
// `useReducer`/`useContext` directly, so the reducer + z-scale stay the
// single source of truth.
//
// State and dispatch are split into two Contexts (react-state-design §8,
// "Context as broadcast, not a store") so a future write-only consumer (a
// nav button that only needs to call `openUtility()`) doesn't need to
// subscribe to `overlay` value changes it never reads.
//
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §C.1 (two-slot amendment), §C.2 (context layer this implements)
// @see .tondocs/skills/react-state-design.md §8, §9 (Context split, hook API)

import { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from "react";
import {
  reduceOverlay,
  INITIAL_OVERLAY_STATE,
  type OverlayState,
  type OverlayAction,
  type PanelOverlay,
  type UtilityOverlay,
} from "@/lib/overlay/overlay-state";

const OverlayStateContext = createContext<OverlayState | undefined>(undefined);
const OverlayDispatchContext = createContext<React.Dispatch<OverlayAction> | undefined>(undefined);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [overlay, dispatch] = useReducer(reduceOverlay, INITIAL_OVERLAY_STATE);

  return (
    <OverlayStateContext.Provider value={overlay}>
      <OverlayDispatchContext.Provider value={dispatch}>{children}</OverlayDispatchContext.Provider>
    </OverlayStateContext.Provider>
  );
}

function useOverlayState(): OverlayState {
  const overlay = useContext(OverlayStateContext);
  if (overlay === undefined) {
    throw new Error("useOverlay/usePanelIs/useUtilityIs must be used within an OverlayProvider");
  }
  return overlay;
}

function useOverlayDispatch(): React.Dispatch<OverlayAction> {
  const dispatch = useContext(OverlayDispatchContext);
  if (dispatch === undefined) {
    throw new Error("useOverlay must be used within an OverlayProvider");
  }
  return dispatch;
}

/**
 * Read + write API for the app-shell overlay slots.
 *
 * All actions are wrapped in `useCallback` over the reducer `dispatch`
 * (itself stable across renders per the `useReducer` contract), so they are
 * safe to list in a consumer's `useEffect`/`useCallback` deps without
 * causing re-subscription churn.
 *
 * - `openPanel(p)` replaces the panel slot AND closes an open utility
 *   (§C.1 policy — a new panel context invalidates the sheet above it).
 * - `updatePanel(p)` replaces the panel's PAYLOAD in place — same `kind`
 *   only, no-op otherwise — WITHOUT touching the utility slot. Use this for
 *   an already-open panel's own content drifting (e.g. snap-feed details
 *   following scroll), never for a user-initiated new panel open (that's
 *   `openPanel`, which intentionally cascades the utility close).
 * - `openUtility(u)` replaces only the utility slot.
 * - `close()` closes the topmost non-none slot (utility first, then panel).
 * - `closePanel()` / `closeUtility()` reset exactly that slot.
 */
export function useOverlay(): {
  overlay: OverlayState;
  openPanel: (panel: Exclude<PanelOverlay, { kind: "none" }>) => void;
  updatePanel: (panel: Exclude<PanelOverlay, { kind: "none" }>) => void;
  openUtility: (utility: Exclude<UtilityOverlay, { kind: "none" }>) => void;
  close: () => void;
  closePanel: () => void;
  closeUtility: () => void;
} {
  const overlay = useOverlayState();
  const dispatch = useOverlayDispatch();

  const openPanel = useCallback(
    (panel: Exclude<PanelOverlay, { kind: "none" }>) => dispatch({ type: "openPanel", panel }),
    [dispatch],
  );
  const updatePanel = useCallback(
    (panel: Exclude<PanelOverlay, { kind: "none" }>) => dispatch({ type: "updatePanel", panel }),
    [dispatch],
  );
  const openUtility = useCallback(
    (utility: Exclude<UtilityOverlay, { kind: "none" }>) => dispatch({ type: "openUtility", utility }),
    [dispatch],
  );
  const close = useCallback(() => dispatch({ type: "close" }), [dispatch]);
  const closePanel = useCallback(() => dispatch({ type: "closePanel" }), [dispatch]);
  const closeUtility = useCallback(() => dispatch({ type: "closeUtility" }), [dispatch]);

  return useMemo(
    () => ({ overlay, openPanel, updatePanel, openUtility, close, closePanel, closeUtility }),
    [overlay, openPanel, updatePanel, openUtility, close, closePanel, closeUtility],
  );
}

/**
 * Selector hook for consumers that only need to know "is THIS panel kind
 * open" (e.g. a derived `hideNavBar = usePanelIs(...) || ...` per §C.2).
 * Reads only the state Context — components that only ever call
 * `openPanel`/`closePanel` and never render off `overlay` should prefer
 * this over destructuring `overlay` out of `useOverlay()`.
 */
export function usePanelIs(kind: PanelOverlay["kind"]): boolean {
  const overlay = useOverlayState();
  return overlay.panel.kind === kind;
}

/** Same as {@link usePanelIs}, for the utility slot. */
export function useUtilityIs(kind: UtilityOverlay["kind"]): boolean {
  const overlay = useOverlayState();
  return overlay.utility.kind === kind;
}
